import type { AnyID, Item } from '@types'
import { Quaternion, Vector3 } from 'three'
import { resolveLayoutWidth } from '@utils/linkedArms/armPlacement'
import { isPouff } from './rinconeroUtils'
import type { PouffPenetrationHit } from './pouffDragCollision'

const isArmCatalog = (catalogID: unknown): boolean =>
  /^BR\d+(-\d+)?$/i.test(String(catalogID || '').trim())

export type PouffArmApproachMetrics = {
  xProj: number
  zProj: number
  exteriorSign: 1 | -1
  halfX: number
  halfZ: number
  puffExtentZ: number
  onExteriorSide: boolean
  alongsideArmZ: boolean
  distToExteriorFace: number
  distToNearestZFace: number
  lateralApproach: boolean
}

function computePouffExtentAlongAxis(
  puff: Item,
  puffQuat: Quaternion,
  axis: Vector3,
): number {
  const puffX = new Vector3(1, 0, 0).applyQuaternion(puffQuat)
  const puffZ = new Vector3(0, 0, 1).applyQuaternion(puffQuat)
  const puffWidth = Number(puff.width || 0)
  const puffDepth = Number(puff.thickness || 0)
  return (
    (puffWidth / 2) * Math.abs(puffX.dot(axis)) +
    (puffDepth / 2) * Math.abs(puffZ.dot(axis))
  )
}

export function computePouffArmApproachMetrics(
  arm: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
): PouffArmApproachMetrics | null {
  if (!arm.matrix || !puff.matrix) return null

  const armPos = new Vector3()
  const armQuat = new Quaternion()
  arm.matrix.decompose(armPos, armQuat, new Vector3())
  const armXAxis = new Vector3(1, 0, 0).applyQuaternion(armQuat)
  const armZAxis = new Vector3(0, 0, 1).applyQuaternion(armQuat)

  const puffQuat = new Quaternion()
  puff.matrix.decompose(new Vector3(), puffQuat, new Vector3())
  const puffCenter = new Vector3(puff.width / 2, puff.height / 2, puff.thickness / 2)
    .applyQuaternion(puffQuat)
    .add(draggedCornerPos)

  const armCenter = new Vector3(arm.width / 2, arm.height / 2, arm.thickness / 2)
    .applyQuaternion(armQuat)
    .add(armPos)

  const exteriorSign = getArmExteriorXSign(arm, items)
  const halfX = Number(arm.width || 0) / 2
  const halfZ = Number(arm.thickness || 0) / 2
  const puffExtentZ = computePouffExtentAlongAxis(puff, puffQuat, armZAxis)

  // Borde del puff que mira hacia el brazo (no el lado opuesto al exterior).
  const towardArm = armXAxis.clone().multiplyScalar(-exteriorSign)
  const puffExtentTowardArm = computePouffExtentAlongAxis(puff, puffQuat, towardArm)
  const approachProbe = puffCenter.clone().add(towardArm.multiplyScalar(puffExtentTowardArm))
  const probeDelta = approachProbe.clone().sub(armCenter)
  probeDelta.y = 0
  const probeXProj = armXAxis.dot(probeDelta)
  const probeZProj = armZAxis.dot(probeDelta)

  const onExteriorSide = probeXProj * exteriorSign > 0
  const alongsideArmZ = Math.abs(probeZProj) <= halfZ + puffExtentZ
  const distToExteriorFace = Math.abs(exteriorSign * probeXProj - halfX)
  const distToNearestZFace = Math.max(0, Math.abs(probeZProj) - halfZ - puffExtentZ)
  const lateralApproach = Math.abs(probeXProj) >= Math.abs(probeZProj) * 0.65

  return {
    xProj: probeXProj,
    zProj: probeZProj,
    exteriorSign,
    halfX,
    halfZ,
    puffExtentZ,
    onExteriorSide,
    alongsideArmZ,
    distToExteriorFace,
    distToNearestZFace,
    lateralApproach,
  }
}

export function isArmAnchorForPouff(item: Item | undefined | null): boolean {
  return Boolean(item && isArmCatalog(item.catalogID))
}

/** Lado exterior del brazo respecto a su módulo padre (+1 = brazo a la derecha del padre en eje X local). */
export function getArmExteriorXSign(arm: Item, items: Map<AnyID, Item>): 1 | -1 {
  const parentId = (arm as Item & { firstParentID?: AnyID }).firstParentID
  const parent = parentId ? items.get(parentId as AnyID) : undefined
  if (!parent?.matrix || !arm.matrix) return 1

  const armPos = new Vector3()
  const armQuat = new Quaternion()
  arm.matrix.decompose(armPos, armQuat, new Vector3())

  const parentPos = new Vector3()
  const parentQuat = new Quaternion()
  parent.matrix.decompose(parentPos, parentQuat, new Vector3())

  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
  return armPos.clone().sub(parentPos).dot(parentXAxis) >= 0 ? 1 : -1
}

/** El lado del brazo está ocupado por su módulo padre (cara interior), no por otro item. */
export function isArmInteriorParentOccupant(arm: Item, sideOccupant: string | undefined): boolean {
  if (!sideOccupant) return false
  const parentId = (arm as Item & { firstParentID?: AnyID }).firstParentID
  return Boolean(parentId && String(parentId) === String(sideOccupant))
}

/** El puff intenta entrar por un lateral del brazo ya ocupado por otra unidad. */
export function isPouffApproachBlockedByArmOccupants(
  arm: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
  puffId?: AnyID,
): boolean {
  const metrics = computePouffArmApproachMetrics(arm, puff, draggedCornerPos, items)
  if (!metrics) return true

  const snappedSides =
    (arm as Item & { snappedSides?: Partial<Record<'left' | 'right', string>> }).snappedSides ||
    {}
  const parentId = (arm as Item & { firstParentID?: AnyID }).firstParentID

  for (const side of ['left', 'right'] as const) {
    const occupantId = snappedSides[side]
    if (!occupantId) continue
    if (parentId && String(parentId) === String(occupantId)) continue
    if (puffId && String(puffId) === String(occupantId)) continue

    const occupant = items.get(occupantId as AnyID)
    if (!occupant?.matrix) continue

    const sideSign: 1 | -1 = side === 'left' ? -1 : 1
    const approachingFromOccupiedSide =
      metrics.xProj * sideSign > metrics.halfX * 0.25 &&
      Math.abs(metrics.zProj) <= metrics.halfZ + metrics.puffExtentZ + 40

    if (approachingFromOccupiedSide) return true
  }

  return false
}

/** Solo ignorar al padre cuando el puff ya está en la ranura exterior del brazo. */
export function shouldIgnoreParentForPouffArmDrag(
  metrics: PouffArmApproachMetrics,
): boolean {
  return (
    metrics.onExteriorSide &&
    metrics.alongsideArmZ &&
    metrics.distToExteriorFace <= 70
  )
}

/**
 * Durante el arrastre: no bloquear con el brazo si el puff viene por la cara exterior libre.
 * El módulo padre solo se ignora en la ranura exterior (evita atravesar el recto).
 */
export function shouldPouffIgnoreArmObstaclesForDrag(
  arm: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
  puffId?: AnyID,
): boolean {
  const metrics = computePouffArmApproachMetrics(arm, puff, draggedCornerPos, items)
  if (!metrics) return false

  if (isPouffApproachBlockedByArmOccupants(arm, puff, draggedCornerPos, items, puffId)) {
    return false
  }

  if (!metrics.onExteriorSide) return false

  const snapReachMm =
    Math.max(Number(puff.width || 0), Number(puff.thickness || 0)) + 100

  if (metrics.alongsideArmZ && metrics.distToExteriorFace <= snapReachMm) return true
  return metrics.lateralApproach && metrics.distToExteriorFace <= snapReachMm
}

/**
 * Solo pegar al brazo cuando el puff se acerca por la cara exterior (lateral),
 * no cuando pasa por delante/detrás del recto cerca del borde del brazo.
 */
export function shouldPouffSnapToArm(
  arm: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
): boolean {
  const metrics = computePouffArmApproachMetrics(arm, puff, draggedCornerPos, items)
  if (!metrics?.onExteriorSide) return false
  if (isPouffApproachBlockedByArmOccupants(arm, puff, draggedCornerPos, items, puff.id)) {
    return false
  }
  if (metrics.alongsideArmZ) return true
  return metrics.distToExteriorFace < metrics.distToNearestZFace
}

/**
 * Posición de esquina del puff pegado a la cara exterior del brazo (misma lógica que al dibujar).
 * En Z permite deslizar a lo largo del brazo según la posición arrastrada.
 */
export function computePouffSnapPositionToArm(
  arm: Item,
  puff: Item,
  draggedCornerPos: Vector3,
  items: Map<AnyID, Item>,
): Vector3 | null {
  if (!arm.matrix || !puff.matrix) return null

  const armPos = new Vector3()
  const armQuat = new Quaternion()
  arm.matrix.decompose(armPos, armQuat, new Vector3())
  const armXAxis = new Vector3(1, 0, 0).applyQuaternion(armQuat)
  const armZAxis = new Vector3(0, 0, 1).applyQuaternion(armQuat)

  const exteriorSign = getArmExteriorXSign(arm, items)
  const armWidth = resolveLayoutWidth(arm) || Number(arm.width || 0)
  const puffWidth = Number(puff.width || 0)
  const puffDepth = Number(puff.thickness || 0)
  const widthToUse = exteriorSign > 0 ? armWidth : puffWidth

  const snappedCorner = armPos
    .clone()
    .add(armXAxis.clone().multiplyScalar(exteriorSign * widthToUse))

  const puffQuat = new Quaternion()
  puff.matrix.decompose(new Vector3(), puffQuat, new Vector3())
  const puffExtentAlongArmZ = computePouffExtentAlongAxis(puff, puffQuat, armZAxis)

  const dragDelta = draggedCornerPos.clone().sub(armPos)
  const dragZAlongArm = armZAxis.dot(dragDelta)
  const armLength = Number(arm.thickness || 0)
  const minZAlong = 0
  const maxZAlong = Math.max(minZAlong, armLength - puffExtentAlongArmZ * 2)
  const clampedZAlong = Math.max(minZAlong, Math.min(maxZAlong, dragZAlongArm))

  snappedCorner.add(armZAxis.clone().multiplyScalar(clampedZAlong))
  snappedCorner.y = draggedCornerPos.y
  return snappedCorner
}

/** Al acercarse al exterior de un brazo, el brazo (y a veces el padre) no bloquean el arrastre. */
export function buildPouffDragIgnoredObstacleIds(
  draggedCornerPos: Vector3,
  draggedItem: Item,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  puffId?: AnyID,
): Set<string> | undefined {
  const ignored = new Set<string>()

  for (const otherId of collisionableIds) {
    const other = items.get(otherId)
    if (!other || !isArmAnchorForPouff(other)) continue
    if (
      !shouldPouffIgnoreArmObstaclesForDrag(
        other,
        draggedItem,
        draggedCornerPos,
        items,
        puffId,
      )
    ) {
      continue
    }

    ignored.add(String(other.id))

    const metrics = computePouffArmApproachMetrics(
      other,
      draggedItem,
      draggedCornerPos,
      items,
    )
    const parentId = (other as Item & { firstParentID?: AnyID }).firstParentID
    if (parentId && metrics && shouldIgnoreParentForPouffArmDrag(metrics)) {
      ignored.add(String(parentId))
    }
  }

  return ignored.size > 0 ? ignored : undefined
}

const ARM_IGNORE_RELEASE_EXTRA_MM = 80

/**
 * Misma lógica que buildPouffDragIgnoredObstacleIds pero con histéresis:
 * si el frame anterior ya ignoraba un brazo, no dejar de ignorarlo de golpe al cruzar el umbral.
 */
export function buildStablePouffDragIgnoredObstacleIds(
  draggedCornerPos: Vector3,
  draggedItem: Item,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  previousIgnored?: ReadonlySet<string>,
  puffId?: AnyID,
): Set<string> | undefined {
  const ignored = new Set<string>()
  const snapReachMm =
    Math.max(Number(draggedItem.width || 0), Number(draggedItem.thickness || 0)) + 100
  const releaseReachMm = snapReachMm + ARM_IGNORE_RELEASE_EXTRA_MM

  for (const otherId of collisionableIds) {
    const other = items.get(otherId)
    if (!other || !isArmAnchorForPouff(other)) continue

    const otherKey = String(other.id)
    const parentId = (other as Item & { firstParentID?: AnyID }).firstParentID
    const parentKey = parentId ? String(parentId) : null

    const shouldIgnore = shouldPouffIgnoreArmObstaclesForDrag(
      other,
      draggedItem,
      draggedCornerPos,
      items,
      puffId,
    )
    if (shouldIgnore) {
      ignored.add(otherKey)
      const metrics = computePouffArmApproachMetrics(
        other,
        draggedItem,
        draggedCornerPos,
        items,
      )
      if (parentKey && metrics && shouldIgnoreParentForPouffArmDrag(metrics)) {
        ignored.add(parentKey)
      }
      continue
    }

    if (!previousIgnored?.has(otherKey)) continue

    const metrics = computePouffArmApproachMetrics(other, draggedItem, draggedCornerPos, items)
    if (
      metrics &&
      metrics.onExteriorSide &&
      metrics.distToExteriorFace <= releaseReachMm &&
      metrics.alongsideArmZ &&
      !isPouffApproachBlockedByArmOccupants(other, draggedItem, draggedCornerPos, items, puffId)
    ) {
      ignored.add(otherKey)
      if (parentKey && shouldIgnoreParentForPouffArmDrag(metrics)) {
        ignored.add(parentKey)
      }
    }
  }

  return ignored.size > 0 ? ignored : undefined
}

/**
 * Al validar snap del puff contra un brazo BR, ignorar penetración con el módulo padre
 * y con el propio brazo (contacto cara a cara esperado).
 */
export function filterPouffPenetrationsForArmAnchor(
  anchor: Item | undefined | null,
  penetrations: PouffPenetrationHit[],
): PouffPenetrationHit[] {
  if (!anchor) return penetrations
  const exclude = new Set<string>([String(anchor.id)])
  if (isArmAnchorForPouff(anchor)) {
    const parentId = String((anchor as Item & { firstParentID?: AnyID }).firstParentID || '')
    if (parentId) exclude.add(parentId)
  }
  return penetrations.filter((hit) => !exclude.has(String(hit.otherId)))
}

export function pouffSnapTargetIsArm(anchor: Item, dragged: Item): boolean {
  return isPouff(dragged.catalogID) && isArmAnchorForPouff(anchor)
}

const ARM_TARGET_SWITCH_HYSTERESIS_MM = 120

/** Brazo exterior al que el usuario se acerca durante el arrastre (con histéresis). */
export function pickPouffTargetArmId(
  corner: Vector3,
  puff: Item,
  puffId: AnyID,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  previousArmId?: AnyID | null,
): AnyID | null {
  type ArmCandidate = { id: AnyID; dist: number }
  const candidates: ArmCandidate[] = []

  for (const otherId of collisionableIds) {
    if (otherId === puffId) continue
    const other = items.get(otherId)
    if (!other || !isArmAnchorForPouff(other)) continue

    const metrics = computePouffArmApproachMetrics(other, puff, corner, items)
    if (!metrics?.onExteriorSide) continue
    if (isPouffApproachBlockedByArmOccupants(other, puff, corner, items, puffId)) continue

    candidates.push({ id: otherId, dist: metrics.distToExteriorFace })
  }

  if (!candidates.length) return null
  candidates.sort((a, b) => a.dist - b.dist)

  const best = candidates[0]
  if (!previousArmId) return best.id

  const previous = candidates.find((c) => String(c.id) === String(previousArmId))
  if (!previous) return best.id
  if (previous.dist - best.dist <= ARM_TARGET_SWITCH_HYSTERESIS_MM) return previous.id
  return best.id
}
